/** GET/PUT /api/user-settings/default-signature and reply-forward-default-signature */

export type DefaultSignatureKind = "new-mail" | "reply-forward";

export type DefaultSignatureApiDetails = {
  url: string;
  httpStatus: number;
  httpOk: boolean;
  rawBodySnippet: string;
  /** Nur wenn HTTP OK und JSON parsebar mit gültiger Zahl */
  parsedDefaultSignatureId: number | null;
  orgDefaultSignatureId: number | null;
  isDirty: boolean;
  /** true = Standardsignatur ist gesetzt, aber nur für fremde Entra-Gruppen freigegeben */
  blockedByAudience: boolean;
};

export type ResolvedOrgDefaultSignatureIds = {
  newMail: number | null;
  replyForward: number | null;
};

function defaultSignaturePath(kind: DefaultSignatureKind): string {
  return kind === "reply-forward"
    ? "/api/user-settings/reply-forward-default-signature"
    : "/api/user-settings/default-signature";
}

function orgDefaultSignaturePath(kind: DefaultSignatureKind): string {
  return kind === "reply-forward"
    ? "/api/user-settings/org-reply-forward-default-signature"
    : "/api/user-settings/org-default-signature";
}

function parseId(value: unknown): number | null {
  if (typeof value === "number" && !isNaN(value) && value > 0) return value;
  if (value != null && value !== "") {
    const n = parseInt(String(value).trim(), 10);
    if (!isNaN(n) && n > 0) return n;
  }
  return null;
}

function parseDefaultSignatureResponse(txt: string): Pick<
  DefaultSignatureApiDetails,
  "parsedDefaultSignatureId" | "orgDefaultSignatureId" | "isDirty" | "blockedByAudience"
> {
  let parsedDefaultSignatureId: number | null = null;
  let orgDefaultSignatureId: number | null = null;
  let isDirty = false;
  let blockedByAudience = false;
  try {
    const j = JSON.parse(txt) as {
      defaultSignatureId?: number | string | null;
      orgDefaultSignatureId?: number | string | null;
      isDirty?: boolean;
      blockedByAudience?: boolean;
    };
    blockedByAudience = j?.blockedByAudience === true;
    isDirty = j?.isDirty === true;
    parsedDefaultSignatureId = parseId(j?.defaultSignatureId);
    orgDefaultSignatureId = parseId(j?.orgDefaultSignatureId);
  } catch {
    /* rawBodySnippet enthält dann den Rohtext */
  }
  return { parsedDefaultSignatureId, orgDefaultSignatureId, isDirty, blockedByAudience };
}

function parseOrgDefaultSignatureResponse(txt: string): number | null {
  try {
    const j = JSON.parse(txt) as { orgDefaultSignatureId?: number | string | null };
    return parseId(j?.orgDefaultSignatureId);
  } catch {
    return null;
  }
}

/**
 * GET default-signature mit Diagnose (Status, Antworttext, geparste ID).
 * Nutzt .text() + JSON.parse, damit auch nicht-JSON-Fehlertexte sichtbar werden.
 */
export async function fetchDefaultSignatureDetailsFromApi(
  apiBase: string,
  token: string,
  kind: DefaultSignatureKind = "new-mail"
): Promise<DefaultSignatureApiDetails> {
  const base = apiBase.replace(/\/+$/, "");
  const url = `${base}${defaultSignaturePath(kind)}`;
  let httpStatus = 0;
  let httpOk = false;
  let rawBodySnippet = "";
  let parsedDefaultSignatureId: number | null = null;
  let orgDefaultSignatureId: number | null = null;
  let isDirty = false;
  let blockedByAudience = false;
  try {
    const r = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
      cache: "no-store",
    });
    httpStatus = r.status;
    httpOk = r.ok;
    const txt = await r.text();
    rawBodySnippet = txt.length > 420 ? `${txt.slice(0, 420)}…` : txt;
    if (httpOk && txt.trim()) {
      const parsed = parseDefaultSignatureResponse(txt);
      parsedDefaultSignatureId = parsed.parsedDefaultSignatureId;
      orgDefaultSignatureId = parsed.orgDefaultSignatureId;
      isDirty = parsed.isDirty;
      blockedByAudience = parsed.blockedByAudience;
    }
  } catch (e) {
    rawBodySnippet = `Netzwerk-/Fetch-Fehler: ${e instanceof Error ? e.message : String(e)}`.slice(0, 420);
  }
  return {
    url,
    httpStatus,
    httpOk,
    rawBodySnippet,
    parsedDefaultSignatureId,
    orgDefaultSignatureId,
    isDirty,
    blockedByAudience,
  };
}

export async function fetchOrgDefaultSignatureIdFromApi(
  apiBase: string,
  token: string,
  kind: DefaultSignatureKind
): Promise<number | null> {
  const base = apiBase.replace(/\/+$/, "");
  const url = `${base}${orgDefaultSignaturePath(kind)}`;
  try {
    const r = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
      cache: "no-store",
    });
    if (!r.ok) return null;
    const txt = await r.text();
    if (!txt.trim()) return null;
    return parseOrgDefaultSignatureResponse(txt);
  } catch {
    return null;
  }
}

export async function fetchResolvedOrgDefaultSignatureIds(
  apiBase: string,
  token: string
): Promise<ResolvedOrgDefaultSignatureIds> {
  const [newMail, replyForward] = await Promise.all([
    fetchOrgDefaultSignatureIdFromApi(apiBase, token, "new-mail"),
    fetchOrgDefaultSignatureIdFromApi(apiBase, token, "reply-forward"),
  ]);
  return { newMail, replyForward };
}

export async function fetchDefaultSignatureIdFromApi(
  apiBase: string,
  token: string,
  kind: DefaultSignatureKind = "new-mail"
): Promise<string | null> {
  const d = await fetchDefaultSignatureDetailsFromApi(apiBase, token, kind);
  return d.parsedDefaultSignatureId != null ? String(d.parsedDefaultSignatureId) : null;
}

export async function saveDefaultSignatureIdToApi(
  apiBase: string,
  token: string,
  signatureId: number | null,
  kind: DefaultSignatureKind = "new-mail"
): Promise<boolean> {
  const base = apiBase.replace(/\/+$/, "");
  const url = `${base}${defaultSignaturePath(kind)}`;
  const r = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ defaultSignatureId: signatureId }),
  });
  return r.ok;
}
